import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import Button from '@atlaskit/button/new';
import ModalDialog, {
  ModalTransition,
  ModalBody,
  ModalTitle,
  ModalHeader,
  ModalFooter,
} from '@atlaskit/modal-dialog';
import { Label } from '@atlaskit/form';
import Textfield from '@atlaskit/textfield';
import SectionMessage from '@atlaskit/section-message';
import { Dimensions } from 'components/visuals';
import { State } from 'model/State';
import {
  selectIsPONumberUpdateModalOpened,
  selectOrderDetailsForPONumberUpdateModal,
  selectPurchaseOrderNumberUpdated,
  selectPurchaseOrderNumberUpdatedResponse,
  selectSwitchedUser,
} from '../duck/selectors';
import { closePONumberUpdateModal } from '../duck/actions/orders';
import { postPurchaseOrderNumberUpdate } from '../duck/actions/cart';

const FieldWrapper = styled.div`
  margin: ${Dimensions.units(2)}px;
`;

const Dialog = () => {
  const dispatch = useDispatch();

  const orderDetails = useSelector(selectOrderDetailsForPONumberUpdateModal);
  const impersonatedUserEmail = useSelector(selectSwitchedUser);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(true);
  const [PONumber, setPONumber] = useState(orderDetails?.cartOrderDetails?.purchaseOrderNumber);
  const [isErrorOnUpdatePONumber, setIsErrorOnUpdatePONumber] = useState(false);

  const updatedPONumber = useSelector((state) =>
    selectPurchaseOrderNumberUpdated(PONumber as string, state as State),
  );

  const updatedPONumberResponse = useSelector((state) =>
    selectPurchaseOrderNumberUpdatedResponse(PONumber as string, state as State),
  );

  useEffect(() => {
    if (updatedPONumber === '') {
      window.location.reload();
    }
  }, [updatedPONumber]);

  useEffect(() => {
    if (updatedPONumberResponse?.error) {
      setIsLoading(false);
      if (updatedPONumberResponse?.error?.response?.status === 403) {
        setHasPermission(false);
      } else {
        setIsErrorOnUpdatePONumber(true);
      }
    } else {
      setHasPermission(true);
      setIsErrorOnUpdatePONumber(false);
    }
  }, [updatedPONumberResponse]);

  const onClose = () => {
    dispatch(closePONumberUpdateModal(orderDetails?.orderId as number));
  };

  const onSave = () => {
    setIsLoading(true);
    setIsErrorOnUpdatePONumber(false);
    dispatch(postPurchaseOrderNumberUpdate(orderDetails?.id?.toString() || '', PONumber as string));
  };

  const handlePONumberInputChange = (e) => {
    setPONumber(e.target.value);
  };

  return (
    <ModalDialog autoFocus shouldCloseOnEscapePress width="medium">
      <ModalHeader>
        <ModalTitle testId="update-po-number-modal-title">
          <FormattedMessage
            id="mac.orders.update-po-number.modal.title"
            defaultMessage="Update PO Number"
          />
        </ModalTitle>
      </ModalHeader>
      <ModalBody>
        <>
          {hasPermission ? (
            <>
              <p>
                <FormattedMessage
                  id="mac.orders.update-po-number.modal.content.p1"
                  defaultMessage="Please note that purchase orders are not accepted as a form of payment and we cannot accept any conflicting terms and conditions associated with the purchase order."
                />
              </p>
              <p>
                <FormattedMessage
                  id="mac.orders.update-po-number.modal.content.p2"
                  defaultMessage="However, we are happy to reference your PO number on your quote / invoice to help with your internal tracking if required."
                />
              </p>
              <FieldWrapper>
                <Label htmlFor="PONumber">
                  <FormattedMessage
                    id="mac.orders.update-po-number.modal.po-number-input-label"
                    defaultMessage="PO Number"
                  />
                </Label>
                <Textfield
                  name="PONumber"
                  id="PONumber"
                  onChange={handlePONumberInputChange}
                  data-cy="mac-orders-update-po-number-po-number-input"
                  width={Dimensions.units(40)}
                  isRequired
                  value={PONumber || ''}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      onSave();
                    }
                  }}
                />
              </FieldWrapper>
              <FieldWrapper>
                <Label htmlFor="yourEmail">
                  <FormattedMessage
                    id="mac.orders.update-po-number.modal.email-input-label"
                    defaultMessage="Your Email"
                  />
                </Label>
                <Textfield
                  name="yourEmail"
                  id="yourEmail"
                  type="email"
                  data-cy="mac-orders-update-po-number-email-input"
                  isDisabled
                  width={Dimensions.units(40)}
                  isRequired
                  placeholder={impersonatedUserEmail}
                />
              </FieldWrapper>
            </>
          ) : (
            <SectionMessage appearance="warning">
              <FormattedMessage
                id="mac.orders.update-po-number.modal.no-permission-message"
                defaultMessage="You do not have permission to access this order."
              />
            </SectionMessage>
          )}
        </>
        {isErrorOnUpdatePONumber && (
          <SectionMessage appearance="error">
            <FormattedMessage
              id="mac.orders.update-po-number.modal.error-message"
              defaultMessage="An error occurred updating your quote. Please try again!"
            />
          </SectionMessage>
        )}
      </ModalBody>
      <ModalFooter>
        <>
          {hasPermission && (
            <Button
              onClick={onSave}
              appearance="primary"
              data-cy="update-po-number-dialog-primary-button"
              isLoading={isLoading}
              isDisabled={
                !PONumber || PONumber === orderDetails?.cartOrderDetails?.purchaseOrderNumber
              }
            >
              <FormattedMessage
                id="mac.orders.update-po-number.modal.button.save"
                defaultMessage="Save"
              />
            </Button>
          )}
          <Button onClick={onClose} appearance="subtle">
            <FormattedMessage
              id="mac.orders.update-po-number.modal.button.close"
              defaultMessage="Close"
            />
          </Button>
        </>
      </ModalFooter>
    </ModalDialog>
  );
};

const UpdatePONumberModal = () => {
  const [isModalOpened, setIsModalOpened] = useState(false);
  const selectIsModalOpened = useSelector(selectIsPONumberUpdateModalOpened);

  useEffect(() => {
    setIsModalOpened(selectIsModalOpened);
  }, [selectIsModalOpened]);

  return <ModalTransition>{isModalOpened && <Dialog />}</ModalTransition>;
};

export default UpdatePONumberModal;
